import { useCallback, useEffect, useRef, useState } from "react";

const normalizeMetering = (metering: number) => {
	const minDb = -60;
	const maxDb = -10;
	const clampedDb = Math.min(maxDb, Math.max(minDb, metering));
	return ((clampedDb - minDb) / (maxDb - minDb)) ** 2;
};

// Helper function to convert Blob to base64
const blobToBase64 = (blob: Blob): Promise<string> => {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => {
			const base64String = reader.result as string;
			// Remove the data URL prefix (e.g., "data:audio/webm;base64,")
			const base64Content = base64String.split(",")[1];
			resolve(base64Content);
		};
		reader.onerror = reject;
		reader.readAsDataURL(blob);
	});
};

export const useWebMetering = (
	intervalInMs = 100,
	chunkIntervalInMs = 2000,
) => {
	const [meterHistory, setMeterHistory] = useState<number[]>([]);
	const [audioChunks, setAudioChunks] = useState<string[]>([]);
	const [isRecording, setIsRecording] = useState<boolean>(false);

	const audioContextRef = useRef<AudioContext | null>(null);
	const analyserRef = useRef<AnalyserNode | null>(null);
	const meterIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const chunkIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const mediaStreamRef = useRef<MediaStream | null>(null);
	const dataArrayRef = useRef<Uint8Array | null>(null);
	const mediaRecorderRef = useRef<MediaRecorder | null>(null);
	const isRecordingRef = useRef<boolean>(false);

	const updateMeterHistory = useCallback((newLevel: number) => {
		setMeterHistory((prev) => [...prev, newLevel]);
	}, []);

	// Create a new recorder for each chunk to ensure proper headers
	const createAndStartRecorder = useCallback(() => {
		if (!mediaStreamRef.current) {
			return null;
		}

		const mediaRecorder = new MediaRecorder(mediaStreamRef.current, {
			mimeType: "audio/webm",
		});

		let chunks: Blob[] = [];

		mediaRecorder.ondataavailable = (event) => {
			if (event.data.size > 0) {
				chunks.push(event.data);
			}
		};

		mediaRecorder.onstop = async () => {
			if (chunks.length === 0) {
				return;
			}

			// Combine all chunks into a single blob to ensure it's a complete audio file
			const completeBlob = new Blob(chunks, { type: "audio/webm" });

			try {
				const base64Data = await blobToBase64(completeBlob);
				setAudioChunks((prev) => [...prev, base64Data]);
			} catch (error) {
				console.error("Error converting blob to base64:", error);
			}

			chunks = [];
		};

		mediaRecorder.start();
		return mediaRecorder;
	}, []);

	const startRecording = useCallback(() => {
		if (!(mediaStreamRef.current && isRecordingRef.current)) {
			return;
		}

		setIsRecording(true);

		// Start the first recorder
		mediaRecorderRef.current = createAndStartRecorder();

		// Set up interval for creating new chunks
		chunkIntervalRef.current = setInterval(() => {
			if (!isRecordingRef.current) {
				return;
			}

			// Stop the current recorder to finalize the chunk
			if (
				mediaRecorderRef.current &&
				mediaRecorderRef.current.state !== "inactive"
			) {
				mediaRecorderRef.current.stop();
			}

			// Create a new recorder for the next chunk
			mediaRecorderRef.current = createAndStartRecorder();
		}, chunkIntervalInMs);
	}, [chunkIntervalInMs, createAndStartRecorder]);

	const stopRecording = useCallback(() => {
		setIsRecording(false);

		if (chunkIntervalRef.current) {
			clearInterval(chunkIntervalRef.current);
			chunkIntervalRef.current = null;
		}

		if (
			mediaRecorderRef.current &&
			mediaRecorderRef.current.state !== "inactive"
		) {
			mediaRecorderRef.current.stop();
			mediaRecorderRef.current = null;
		}
	}, []);

	const startMetering = useCallback(async () => {
		if (!navigator?.mediaDevices?.getUserMedia) {
			return;
		}

		const stream = await navigator.mediaDevices.getUserMedia({
			audio: {
				echoCancellation: true,
				noiseSuppression: true,
				sampleRate: 128000,
			},
		});
		mediaStreamRef.current = stream;
		isRecordingRef.current = true;

		// Start recording automatically when metering starts
		startRecording();

		audioContextRef.current =
			new // @ts-expect-error does not detect webkitAudioContext
			(window.AudioContext || window.webkitAudioContext)();

		await audioContextRef.current.resume();
		const source = audioContextRef.current.createMediaStreamSource(stream);

		analyserRef.current = audioContextRef.current.createAnalyser();
		analyserRef.current.fftSize = 2048;
		source.connect(analyserRef.current);

		dataArrayRef.current = new Uint8Array(analyserRef.current.fftSize);

		meterIntervalRef.current = setInterval(() => {
			const analyser = analyserRef.current;
			const dataArray = dataArrayRef.current;

			if (!(analyser && dataArray)) {
				return;
			}

			// biome-ignore lint/suspicious/noExplicitAny: dataArray typed array conversion
			analyser.getByteTimeDomainData(dataArray as any);

			let sum = 0;
			for (const value of dataArray) {
				const v = (value - 128) / 128;
				sum += v * v;
			}
			const rms = Math.sqrt(sum / dataArray.length);
			const newLevel = 20 * Math.log10(rms || 0.0001);

			updateMeterHistory(normalizeMetering(newLevel));
		}, intervalInMs);
	}, [updateMeterHistory, intervalInMs, startRecording]);

	const stopMetering = useCallback(async () => {
		isRecordingRef.current = false;

		// Stop recording if it's active
		stopRecording();

		if (meterIntervalRef.current) {
			clearInterval(meterIntervalRef.current);
			meterIntervalRef.current = null;
		}

		if (mediaStreamRef.current) {
			for (const track of mediaStreamRef.current.getTracks()) {
				track.stop();
				mediaStreamRef.current.removeTrack(track);
			}
			mediaStreamRef.current = null;
		}
		if (audioContextRef.current) {
			await audioContextRef.current.close();
			audioContextRef.current = null;
		}
		analyserRef.current = null;
		dataArrayRef.current = null;
	}, [stopRecording]);

	const clearMeterHistory = useCallback(() => {
		setMeterHistory([]);
	}, []);

	const clearAudioChunks = useCallback(() => {
		setAudioChunks([]);
	}, []);

	useEffect(() => {
		return () => {
			stopMetering();
		};
	}, [stopMetering]);

	return {
		meterHistory,
		audioChunks,
		isRecording,
		startMetering,
		stopMetering,
		startRecording,
		stopRecording,
		clearMeterHistory,
		clearAudioChunks,
	};
};
